import React, { useState } from "react";
import { Globe, 
  Phone, 
  Wifi, 
  Server, 
  Satellite, 
  Users, 
  Briefcase, 
  Smartphone,
  Award,
  Shield,
  Headphones } from "lucide-react";
import { BusinessPageSEO } from "../components/SEO";
import "./BusinessPage.css";
import emailjs from "@emailjs/browser";

export default function BusinessPage() {
  const [sending, setSending] = useState(false);

  const offerings = [
    {
      id: 1,
      title: "VoIP (Voice Over Internet Protocol)",
      icon: <Phone />,
      description:
        "VoIP enables phone calls to be made over the internet rather than through traditional telephone lines. This solution helps reduce call costs, especially for long-distance and international communication.",
    },
    {
      id: 2,
      title: "Fibre Connectivity",
      icon: <Wifi />,
      description:
        "Experience high-speed, reliable internet connectivity with Fibre solutions designed to support small to large-scale business operations. Perfect for video conferencing, cloud computing, and seamless communication.",
    },
    {
      id: 3,
      title: "PABX Systems",
      icon: <Server />,
      description:
        "Our PABX (Private Automatic Branch Exchange) systems streamline internal and external communication for businesses, offering features like call forwarding, voicemail, and call routing.",
    },
    {
      id: 4,
      title: "Direct Fibre",
      icon: <Globe />,
      description:
        "Direct Fibre provides dedicated internet lines for enterprises that need guaranteed speeds, uptime, and superior data reliability — ideal for corporate networks and call centres.",
    },
    {
      id: 5,
      title: "Satellite Services",
      icon: <Satellite />,
      description:
        "Our satellite internet services ensure connectivity in remote areas where fibre may not reach, providing dependable communication for businesses across South Africa.",
    },
    {
      id: 6,
      title: "YEP Digital Solutions",
      icon: <Briefcase />,
      description:
        "YEP helps businesses build an online presence with digital listings, marketing, and advertising tools that enhance visibility and customer engagement.",
    },
    {
      id: 7,
      title: "Call Centre Solutions",
      icon: <Users />,
      description:
        "Our call centre solutions are designed to handle large volumes of inbound and outbound calls, enhancing customer service efficiency and business communication.",
    },
    {
      id: 8,
      title: "Mobile Packages",
      icon: <Smartphone />,
      description:
        "We offer customized mobile packages through Telkom, designed to keep your workforce connected on the move with affordable data and voice plans.",
    },
  ];

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    const form = e.target;

    // Get form values
    const templateParams = {
      company_name: form["business-name"].value,
      email: form["business-email"].value,
      phone: form["business-phone"].value,
      subject: form["business-subject"].value,
      message: form["business-message"].value,
    };

    console.log("Sending email with data:", templateParams);

    try {
      const result = await emailjs.send(
        "service_096xkyv",        // EmailJS Service ID
        "template_mqyza9f",       // EmailJS Template ID
        templateParams,           
        "vWkJpQqvu_4nMnMTT"      // EmailJS Public Key
      );

      console.log("Email sent successfully:", result);
      alert("Message sent successfully! We'll get back to you soon.");
      form.reset();
    } catch (error) {
      console.error("Email send error:", error);
      alert("Failed to send message. Please try again or contact us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="business-page">
      <BusinessPageSEO />
      
      {/* Hero Section */}
      <section className="business-hero">
        <div className="container hero-content">
          <h1 className="business-page-title">
            Empowering Businesses with Telkom Solutions
          </h1>
          <p className="business-page-sub">
            From Fibre to VoIP, Migarda delivers innovative solutions to keep your business connected and growing.
          </p>
        </div>
      </section>

      {/* Offerings Section */}
      <section className="offerings-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Services</span>
            <h2 className="section-title">Comprehensive Business Solutions</h2>
            <p className="section-description">
              Everything your business needs to stay connected, productive, and competitive in today's digital world.
            </p>
          </div>
          <div className="offerings-grid">
            {offerings.map((item) => (
              <div key={item.id} className="offering-card">
                <div className="offering-icon-wrapper">
                  {item.icon}
                </div>
                <h3 className="offering-title">{item.title}</h3>
                <p className="offering-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Migarda Section */}
      <section className="why-migarda">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Why Choose Us</span>
          </div>
          <div className="why-grid">
            <div className="why-card">
              <div className="why-card-icon">
                <Award className="why-icon" />
              </div>
              <h3>Telkom Partnership</h3>
              <p>
                As an authorized Telkom partner, we provide access to the latest communication and connectivity
                technologies — directly supported by one of South Africa's leading network providers.
              </p>
            </div>
            <div className="why-card">
              <div className="why-card-icon">
                <Shield className="why-icon" />
              </div>
              <h3>Tailored Solutions</h3>
              <p>
                Whether you're a startup or an established enterprise, our packages are flexible and scalable to match
                your business goals and budget requirements.
              </p>
            </div>
            <div className="why-card">
              <div className="why-card-icon">
                <Headphones className="why-icon" />
              </div>
              <h3>End-to-End Support</h3>
              <p>
                From setup to maintenance, our dedicated team ensures smooth operation and reliable support for all your
                business services with 24/7 availability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Form Section */}
      <section className="business-contact-section">
        <div className="container">
          <div className="contact-form-wrapper">
            <div className="contact-header">
              <h2 className="contact-title">Get In <span className="accent">Touch</span></h2>
              <p className="contact-subtitle">
                Have a question about our business solutions? We'd love to hear from you. 
                Send us a message and we'll respond as soon as possible.
              </p>
            </div>

            <form className="business-contact-form" onSubmit={handleBusinessSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="business-name">Company Name *</label>
                  <input 
                    id="business-name"
                    name="business-name"
                    type="text" 
                    placeholder="Your company name" 
                    required 
                    disabled={sending}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="business-email">Email *</label>
                  <input 
                    id="business-email"
                    name="business-email"
                    type="email" 
                    placeholder="your@company.com" 
                    required 
                    disabled={sending}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="business-phone">Phone *</label>
                  <input 
                    id="business-phone"
                    name="business-phone"
                    type="tel" 
                    placeholder="0800 123 456" 
                    required 
                    disabled={sending}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="business-subject">Subject *</label>
                  <select 
                    id="business-subject" 
                    name="business-subject"
                    required
                    disabled={sending}
                  >
                    <option value="">Select service</option>
                    <option value="VoIP">VoIP</option>
                    <option value="Fibre">Fibre</option>
                    <option value="PABX">PABX</option>
                    <option value="Direct Fibre">Direct Fibre</option>
                    <option value="Satellite">Satellite</option>
                    <option value="Call Centre">Call Centre</option>
                    <option value="Mobile Packages">Mobile Packages</option>
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="business-message">Message *</label>
                <textarea 
                  id="business-message"
                  name="business-message"
                  placeholder="Tell us about your business needs..." 
                  rows="5"
                  required 
                  disabled={sending}
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="contact-submit-btn"
                disabled={sending}
              >
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="business-cta">
        <div className="container">
          <h2>Let's Grow Your Business Together</h2>
          <p>Get in touch with our business team to find the perfect connectivity solution for your organization.</p>
        </div>
      </section>
    </div>
  );
}